import type { FC } from "react";
import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";

import { SWReactIcons } from "@similarweb/icons";
import { shortMonthAndDay } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Banner } from "@similarweb/ui-components";
import { Text, Stack } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { CHART_COLORS } from "app/constants/ChartColors";
import { subtitleFiltersWithCustomColors, useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import DurationService from "app/services/DurationService";

import type { MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode } from "../../../api-client/demandSearchTrendsVolumeListAPI";
import { useKeywordListsNodeAPIQueryParams } from "../../../hooks/useKeywordListsNodeAPIQueryParams";
import { useMonthToDateToggle } from "../../../hooks/useMonthToDateToggle";
import { queryOptionsBuilder } from "../../../utils/queryOptionsBuilder";
import CustomBullet, { CustomBulletContainer } from "../common/CustomBullet";

import { DemandOverTimeChart } from "./DemandOverTimeChart";
import { selectDemandOverTimeData, initDemandOverTimeData } from "./demandOverTimeSelectors";
import { DemandOverTimeWrapper } from "./StyledComponent";

export const DemandOverTime: FC = () => {
    const { params } = useCurrentStateAndParams();
    const { isWindow } = DurationService.getDurationData(params.duration).forAPI;
    const periodOverPeriodType = DurationService.getPeriodOverPeriodCompareType();
    const { isMonthToDateActive, onOffSwitchConfig } = useMonthToDateToggle({
        eventSubName: "Keywords Trends",
        mtdParam: "keywordsTrendsMTD",
    });
    const select = useMemo(() => {
        return (data: MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode) => {
            return selectDemandOverTimeData(data, periodOverPeriodType);
        };
    }, [periodOverPeriodType]);
    const queryParams = useKeywordListsNodeAPIQueryParams({
        isMonthToDate: isMonthToDateActive,
    });
    const queryOptions = useMemo(
        () =>
            queryOptionsBuilder("DemandSearchTrends", "marketResearchDemandAnalysisSearchTrendsList")
                .setSelect(select)
                .setArgs(queryParams)
                .build(),
        [queryParams, select],
    );

    const { data, isLoading, isError } = useQuery(queryOptions);
    const { lineChartData, categories, chartTooltipData, legendItems, totalLegend } = data ?? initDemandOverTimeData;

    const subtitleFilters = useSubtitleFilters("date", "country", "webSource");

    const enrichedSubtitleFilters =
        periodOverPeriodType != null
            ? subtitleFiltersWithCustomColors(
                  subtitleFilters,
                  CHART_COLORS.periodOverPeriod.Total[0],
                  <CustomBulletContainer>
                      <CustomBullet color={CHART_COLORS.periodOverPeriod.Total[0]} />
                  </CustomBulletContainer>,
              )
            : subtitleFilters;

    return (
        <DemandOverTimeWrapper data-automation="DemandOverTime">
            <CustomBullet color={CHART_COLORS.periodOverPeriod.Total[1]} />
            <WidgetContainer
                withBorder
                noData={isError}
                isLoading={isLoading}
                title={translate("demand.analysis.search.trends.demand.over.time.header.title")}
                tooltip={translate("demand.analysis.search.trends.demand.over.time.header.title.tooltip")}
                subtitleFilters={enrichedSubtitleFilters}
                onOffSwitchConfig={onOffSwitchConfig}
            >
                <Stack
                    gap="24px"
                    padding="24px"
                >
                    {isMonthToDateActive ? (
                        <Banner
                            title=""
                            subtitle={
                                <Text fontVariant="body1">
                                    {translate("demand_analysis.search_volume_over_time.month_to_date_banner.title", {
                                        untilDate: dayjs(swSettings.current.windowEndDate).format(shortMonthAndDay),
                                    })}
                                </Text>
                            }
                            CustomIcon={
                                <SWReactIcons
                                    size="xs"
                                    iconName="info"
                                    iconColor={colorsPalettes.carbon[200]}
                                />
                            }
                        />
                    ) : null}
                    <DemandOverTimeChart
                        lineChartData={lineChartData}
                        categories={categories}
                        chartTooltipData={chartTooltipData}
                        periodOverPeriodType={periodOverPeriodType}
                        isWindow={!!isWindow}
                        legendItems={legendItems}
                        totalLegend={totalLegend}
                        isCluster={false}
                        duration={params.duration}
                    />
                </Stack>
            </WidgetContainer>
        </DemandOverTimeWrapper>
    );
};
